import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { ApiCallError, callFunction } from '@/lib/api';
import {
  COPY_RECEIVER_LABEL,
  compactDateTime,
  type EpikriseTextLine,
  lineText,
  ORIGINAL_RECEIVER_LABEL,
  otherDiagnosisLabel,
  periodLabels,
  priorityChip,
  receivedEpikriseHeading,
  RECOMMENDATION_HEADING,
  relationLabel,
  RETTET_BANNER,
} from '@/lib/referral/receivedEpikrise';
import { formatDanishDate } from '@/lib/danishDateTime';
import { EpikrisePriorityChip } from './EpikrisePriorityChip';
import { PartyIdentifier } from './PartyIdentifier';
import { ReceivedMessageCpr } from './ReceivedMessageCpr';

// Spor A T1.4: one received epikrise, in the order DIS01-M 3.2–3.17 /
// DIS02/03-M 3.2–3.18 / DIS05-M 2.2–2.14 ask for. The CPR reaches the browser
// via communicate-epikriser reveal_cpr when the detail opens (audited).

type Party = {
  ean?: string;
  identifier?: string;
  identifierCode?: string;
  organisationName?: string;
  departmentName?: string;
  unitName?: string;
  streetName?: string;
  suburbName?: string;
  districtName?: string;
  postCodeIdentifier?: string;
  telephoneSubscriberIdentifier?: string;
  medicalSpecialityCode?: string;
} | null;
type Dt = { date: string; time: string | null } | null;
type Diagnosis = { code: string; text: string; typeCode: string | null };
type OtherDiagnosis = Diagnosis & { descriptionCode: string; dateTime: Dt };

export type EpikriseDocument = {
  kind: string;
  statusCode: string;
  letterIdentifier: string;
  sentDate: string;
  sentTime: string;
  episodeOfCareIdentifier: string | null;
  sender: Party;
  senderContact: Party;
  receiver: Party;
  ccReceiver: Party;
  patient: {
    idKind: 'cpr' | 'erstatnings_cpr' | 'none';
    personSurnameName: string;
    personGivenName: string | null;
    streetName: string | null;
    suburbName: string | null;
    districtName: string | null;
    postCodeIdentifier: string | null;
    occupancyText: string | null;
    episodeOfCareStatusCode: string | null;
  };
  relatives: Array<{
    relationCode: string;
    hasPersonIdentifier: boolean;
    personSurnameName: string;
    personGivenName: string | null;
    streetName: string | null;
    suburbName: string | null;
    districtName: string | null;
    postCodeIdentifier: string | null;
  }>;
  referral: { identifier: string | null; received: Dt; refer: Diagnosis | null; additional: Diagnosis[] } | null;
  start: Dt;
  end: Dt;
  actionPriorityCodeRaw: string | null;
  actionPriority: string | null;
  mainDiagnosis: Diagnosis | null;
  mainAdditional: Diagnosis[];
  otherDiagnoses: OtherDiagnosis[];
  recommendation: EpikriseTextLine[] | null;
  clinicalInformation: Array<{ signed: Dt; lines: EpikriseTextLine[] }>;
  references: Array<{
    description: string;
    url: string | null;
    sup: boolean;
    bin: { objectIdentifier: string; objectCode: string; objectExtensionCode: string; originalObjectSize: number | null } | null;
  }>;
};

type AttachmentStatus = {
  key: string;
  received: boolean;
  object_extension_code: string | null;
  original_object_size: number | null;
  size_matches: boolean | null;
  received_at: string | null;
};
type LinkedEpikrise = { id: string; exchange_id: string; received_at: string | null; letter_identifier: string | null } | null;

export type EpikriseDetail = {
  row: { id: string; exchange_id: string; received_at: string | null; xsd_ok: boolean | null; patient_id: string | null; match_status: string };
  document: EpikriseDocument;
  attachments: AttachmentStatus[];
  corrects: LinkedEpikrise;
  corrected_by: LinkedEpikrise;
  answers: { id: string; letter_identifier: string; created_at: string; patient_id: string; kind: string } | null;
};

type Download = { file_name: string; mime_type: string; size: number; base64: string };

function partyLines(p: Party): string[] {
  if (!p) return [];
  const address = [p.streetName, [p.postCodeIdentifier, p.districtName].filter(Boolean).join(' '), p.suburbName]
    .filter((v) => v && v.trim().length > 0)
    .join(', ');
  return [p.organisationName, p.departmentName, p.unitName, address, p.telephoneSubscriberIdentifier ? `Tlf. ${p.telephoneSubscriberIdentifier}` : null]
    .filter((v): v is string => Boolean(v && v.trim()));
}

function Line({ line }: { line: EpikriseTextLine }) {
  const align = line.align === 'right' ? 'text-right' : line.align === 'center' ? 'text-center' : '';
  if (line.segments.length === 0 || lineText(line).length === 0) return <br />;
  return (
    <p className={`min-h-[1em] whitespace-pre-wrap ${align}`}>
      {line.segments.map((s, i) => (
        <span
          key={i}
          className={[s.bold ? 'font-bold' : '', s.italic ? 'italic' : '', s.underline ? 'underline' : '', s.fixedFont ? 'font-mono' : '']
            .filter(Boolean)
            .join(' ')}
        >
          {s.text}
        </span>
      ))}
    </p>
  );
}

function Field({ label, children, testId }: { label: string; children: React.ReactNode; testId?: string }) {
  return (
    <div className="grid grid-cols-[12rem_1fr] gap-2 text-sm" data-testid={testId}>
      <dt className="font-medium text-ds-muted">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function diagnosisText(d: Diagnosis): string {
  return `${d.code}${d.text ? ` ${d.text}` : ''}`;
}

function saveBase64(download: Download) {
  const binary = atob(download.base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  const url = URL.createObjectURL(new Blob([bytes], { type: download.mime_type }));
  const opened = window.open(url, '_blank', 'noopener');
  if (!opened) {
    const link = document.createElement('a');
    link.href = url;
    link.download = download.file_name;
    link.click();
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function ReceivedEpikriseDetail({
  clinicId,
  detail,
  onOpenExchange,
}: {
  clinicId: string;
  detail: EpikriseDetail;
  onOpenExchange: (exchangeId: string) => void;
}) {
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const doc = detail.document;
  const chip = priorityChip(doc.actionPriority ?? doc.actionPriorityCodeRaw);
  const period = periodLabels(doc.kind);
  const patientName = [doc.patient.personGivenName, doc.patient.personSurnameName].filter(Boolean).join(' ');
  const patientAddress = [doc.patient.streetName, [doc.patient.postCodeIdentifier, doc.patient.districtName].filter(Boolean).join(' '), doc.patient.suburbName]
    .filter((v) => v && v.trim().length > 0)
    .join(', ');
  const attachmentByKey = new Map(detail.attachments.map((a) => [a.key, a]));

  async function open(objectIdentifier: string) {
    setDownloading(objectIdentifier);
    setDownloadError(null);
    try {
      const result = await callFunction<Record<string, string>, Download>('communicate-epikriser', {
        action: 'download',
        clinic_id: clinicId,
        exchange_id: detail.row.exchange_id,
        object_identifier: objectIdentifier,
      });
      saveBase64(result);
    } catch (err) {
      setDownloadError(err instanceof ApiCallError ? err.message : t('medcom.epikriser.download_failed', 'Bilaget kunne ikke åbnes.'));
    } finally {
      setDownloading(null);
    }
  }

  return (
    <article className="space-y-4 rounded-md border border-ds-line bg-ds-card p-4" data-testid="received-epikrise-detail">
      <h3 className="text-xl font-semibold" data-testid="received-epikrise-heading">
        {receivedEpikriseHeading(doc.kind)}
      </h3>
      {doc.statusCode === 'rettetbrev' && (
        <div className="rounded-md border-2 border-ds-red bg-ds-red-tint p-3 text-sm font-bold uppercase text-ds-red-ink" data-testid="received-epikrise-rettet">
          <p>{RETTET_BANNER}</p>
          {detail.corrects ? (
            <button type="button" className="mt-1 text-xs font-medium normal-case underline" onClick={() => onOpenExchange(detail.corrects!.exchange_id)}>
              {t('medcom.epikriser.open_corrected', 'Vis den tidligere epikrise')} ({detail.corrects.letter_identifier ?? ''})
            </button>
          ) : (
            <p className="mt-1 text-xs font-medium normal-case">
              {t('medcom.epikriser.corrected_not_found', 'Den tidligere epikrise med samme henvisning/forløb er ikke modtaget i Clinio.')}
            </p>
          )}
        </div>
      )}
      {detail.corrected_by && (
        <div className="rounded-md border border-ds-amber-line bg-ds-amber-tint p-3 text-sm" data-testid="received-epikrise-replaced">
          {t('medcom.epikriser.replaced', 'Denne epikrise er erstattet af en rettet epikrise.')}{' '}
          <button type="button" className="underline" onClick={() => onOpenExchange(detail.corrected_by!.exchange_id)}>
            {t('medcom.epikriser.open_rettet', 'Vis den rettede epikrise')}
          </button>
        </div>
      )}
      {detail.answers && (
        <p className="text-sm" data-testid="received-epikrise-answers">
          <a className="text-ds-teal underline" href={`/patients/${detail.answers.patient_id}?tab=medcom`}>
            {`Svar på henvisning ${detail.answers.letter_identifier} sendt ${formatDanishDate(detail.answers.created_at)}`}
          </a>{' '}
          <span className="text-xs text-ds-muted">({detail.answers.kind})</span>
        </p>
      )}

      {(chip || doc.actionPriorityCodeRaw || doc.recommendation) && (
        <section className="space-y-2 rounded-md border-2 border-ds-teal bg-ds-teal-tint p-4" data-testid="epikrise-recommendation">
          <h4 className="text-lg font-semibold">{RECOMMENDATION_HEADING}</h4>
          {chip ? (
            <p className="flex flex-wrap items-center gap-2 text-sm">
              <EpikrisePriorityChip chip={chip} />
              <span>{chip.meaning}</span>
            </p>
          ) : doc.actionPriorityCodeRaw ? (
            <p className="text-sm">{t('medcom.epikriser.priority_unknown', 'Prioritet')}: {doc.actionPriorityCodeRaw}</p>
          ) : (
            <p className="text-sm text-ds-muted">{t('medcom.epikriser.no_priority', 'Ingen markering')}</p>
          )}
          {doc.recommendation && doc.recommendation.length > 0 ? (
            <div className="text-base">{doc.recommendation.map((line, i) => <Line key={i} line={line} />)}</div>
          ) : (
            <p className="text-sm text-ds-muted">{t('medcom.epikriser.no_recommendation', 'Ingen anbefalingstekst i epikrisen.')}</p>
          )}
        </section>
      )}

      <dl className="space-y-1">
        <Field label={t('medcom.epikriser.sender', 'Afsender')} testId="epikrise-sender">
          {partyLines(doc.sender).join(', ')}
          {doc.sender?.ean ? <span className="text-xs text-ds-muted"> · EAN {doc.sender.ean}</span> : null}
          <PartyIdentifier party={doc.sender} />
        </Field>
        {doc.senderContact && partyLines(doc.senderContact).length > 0 && (
          <Field label={t('medcom.epikriser.contact', 'Kontakt')}>{partyLines(doc.senderContact).join(', ')}</Field>
        )}
        <Field label={ORIGINAL_RECEIVER_LABEL} testId="epikrise-receiver">
          {partyLines(doc.receiver).join(', ')}
          {doc.receiver?.ean ? <span className="text-xs text-ds-muted"> · EAN {doc.receiver.ean}</span> : null}
          <PartyIdentifier party={doc.receiver} />
        </Field>
        <Field label={COPY_RECEIVER_LABEL} testId="epikrise-cc">
          {doc.ccReceiver ? (
            <>
              {partyLines(doc.ccReceiver).join(', ')}
              {doc.ccReceiver.ean ? <span className="text-xs text-ds-muted"> · EAN {doc.ccReceiver.ean}</span> : null}
              <PartyIdentifier party={doc.ccReceiver} />
            </>
          ) : (
            t('medcom.epikriser.no_cc', 'Ingen kopimodtager')
          )}
        </Field>
        <Field label={t('medcom.epikriser.sent', 'Afsendt')}>{compactDateTime({ date: doc.sentDate, time: doc.sentTime }) ?? '–'} · {t('medcom.epikriser.letter_id', 'Brev-id')} {doc.letterIdentifier}</Field>
      </dl>

      <dl className="space-y-1 border-t border-ds-line pt-3">
        <Field label={t('medcom.epikriser.patient', 'Patient')} testId="epikrise-patient">
          {patientName}
        </Field>
        <Field
          label={
            doc.patient.idKind === 'erstatnings_cpr'
              ? t('referrals.replacement_cpr', 'Erstatnings-CPR')
              : t('medcom.epikriser.cpr', 'CPR-nummer')
          }
          testId="epikrise-patient-cpr"
        >
          <ReceivedMessageCpr clinicId={clinicId} exchangeId={detail.row.exchange_id} idKind={doc.patient.idKind} />
        </Field>
        {patientAddress && <Field label={t('medcom.epikriser.address', 'Adresse')}>{patientAddress}</Field>}
        {doc.patient.occupancyText && <Field label={t('medcom.epikriser.occupancy', 'Stilling')}>{doc.patient.occupancyText}</Field>}
        {doc.patient.episodeOfCareStatusCode && <Field label={t('medcom.epikriser.episode_status', 'Patientstatus')}>{doc.patient.episodeOfCareStatusCode}</Field>}
        {doc.relatives.map((r, i) => (
          <div key={i} className="contents">
            <Field label={`${t('medcom.epikriser.relative', 'Pårørende')} (${relationLabel(r.relationCode)})`} testId="epikrise-relative">
              {[r.personGivenName, r.personSurnameName].filter(Boolean).join(' ')}
              {[r.streetName, [r.postCodeIdentifier, r.districtName].filter(Boolean).join(' ')].filter((v) => v && v.trim()).length > 0
                ? `, ${[r.streetName, [r.postCodeIdentifier, r.districtName].filter(Boolean).join(' ')].filter((v) => v && v.trim()).join(', ')}`
                : ''}
            </Field>
            {r.hasPersonIdentifier ? (
              <Field label={t('medcom.epikriser.cpr', 'CPR-nummer')} testId="epikrise-relative-cpr">
                <ReceivedMessageCpr
                  clinicId={clinicId}
                  exchangeId={detail.row.exchange_id}
                  idKind="cpr"
                  relativeIndex={i}
                />
              </Field>
            ) : null}
          </div>
        ))}
      </dl>

      {doc.referral && (
        <dl className="space-y-1 border-t border-ds-line pt-3" data-testid="epikrise-referral">
          <Field label={t('medcom.epikriser.referral_id', 'Henvisnings-id')}>{doc.referral.identifier ?? '–'}</Field>
          <Field label={t('medcom.epikriser.referral_received', 'Henvisning modtaget')}>{compactDateTime(doc.referral.received) ?? '–'}</Field>
          {doc.referral.refer && <Field label={t('medcom.epikriser.referral_diagnosis', 'Henvisningsdiagnose')}>{diagnosisText(doc.referral.refer)}</Field>}
          {doc.referral.additional.map((d, i) => (
            <Field key={i} label={t('medcom.epikriser.referral_additional', 'Henvisning, tillægsdiagnose')}>{diagnosisText(d)}</Field>
          ))}
        </dl>
      )}
      {doc.episodeOfCareIdentifier && (
        <dl><Field label="EpisodeOfCareIdentifier">{doc.episodeOfCareIdentifier}</Field></dl>
      )}

      <dl className="space-y-1 border-t border-ds-line pt-3" data-testid="epikrise-period">
        <Field label={period.start}>{compactDateTime(doc.start) ?? '–'}</Field>
        <Field label={period.end}>{compactDateTime(doc.end) ?? '–'}</Field>
      </dl>

      <dl className="space-y-1 border-t border-ds-line pt-3" data-testid="epikrise-diagnoses">
        {doc.mainDiagnosis && <Field label={t('medcom.epikriser.main_diagnosis', 'Aktionsdiagnose')}>{diagnosisText(doc.mainDiagnosis)}</Field>}
        {doc.mainAdditional.map((d, i) => (
          <Field key={`a${i}`} label={t('medcom.epikriser.additional_diagnosis', 'Tillægsdiagnose')}>{diagnosisText(d)}</Field>
        ))}
        {doc.otherDiagnoses.map((d, i) => (
          <Field key={`o${i}`} label={otherDiagnosisLabel(d.descriptionCode)}>
            {diagnosisText(d)}
            {d.dateTime ? <span className="text-xs text-ds-muted"> · {compactDateTime(d.dateTime)}</span> : null}
          </Field>
        ))}
        {!doc.mainDiagnosis && doc.mainAdditional.length === 0 && doc.otherDiagnoses.length === 0 && (
          <p className="text-sm text-ds-muted">{t('medcom.epikriser.no_diagnoses', 'Ingen diagnoser i epikrisen.')}</p>
        )}
      </dl>

      <section className="space-y-3 border-t border-ds-line pt-3" data-testid="epikrise-text">
        <h4 className="font-semibold">{t('medcom.epikriser.text', 'Epikrisetekst')}</h4>
        {doc.clinicalInformation.map((block, i) => (
          <div key={i} className="space-y-0.5 text-sm">
            {block.signed && <p className="text-xs text-ds-muted">{t('medcom.epikriser.signed', 'Signeret')} {compactDateTime(block.signed)}</p>}
            {block.lines.map((line, j) => <Line key={j} line={line} />)}
          </div>
        ))}
      </section>

      {doc.references.length > 0 && (
        <section className="space-y-2 border-t border-ds-line pt-3" data-testid="epikrise-references">
          <h4 className="font-semibold">{t('medcom.epikriser.references', 'Bilag og referencer')}</h4>
          {downloadError && <p role="alert" className="text-sm text-ds-red-ink">{downloadError}</p>}
          <ul className="space-y-1 text-sm">
            {doc.references.map((ref, i) => {
              if (ref.url) {
                return (
                  <li key={i}>
                    {ref.description}:{' '}
                    <a className="text-ds-teal underline" href={ref.url} target="_blank" rel="noreferrer noopener">{ref.url}</a>
                  </li>
                );
              }
              if (ref.bin) {
                const key = ref.bin.objectIdentifier.replace(/[^0-9a-zA-Z]/g, '').toLowerCase();
                const status = attachmentByKey.get(key);
                return (
                  <li key={i} className="flex flex-wrap items-center gap-2" data-testid="epikrise-bin">
                    <span>{ref.description}</span>
                    <span className="font-mono text-xs">{ref.bin.objectIdentifier}.{ref.bin.objectExtensionCode}</span>
                    {status?.received ? (
                      <Button type="button" size="sm" variant="secondary" disabled={downloading === ref.bin.objectIdentifier} onClick={() => void open(ref.bin!.objectIdentifier)}>
                        {t('medcom.epikriser.open_attachment', 'Åbn bilag')}
                      </Button>
                    ) : (
                      <span className="text-xs text-ds-amber-ink">
                        {t('medcom.epikriser.attachment_waiting', 'Bilaget (XBIN01) er ikke modtaget endnu. Det vises her, når det modtages.')}
                      </span>
                    )}
                    {status?.size_matches === false && <span className="text-xs text-ds-amber-ink">{t('medcom.epikriser.size_mismatch', 'Størrelsen passer ikke')}</span>}
                  </li>
                );
              }
              return (
                <li key={i}>
                  {ref.description}
                  {ref.sup ? <span className="text-xs text-ds-muted"> · {t('medcom.epikriser.sup', 'Supplerende oplysninger (SUP) – kan ikke vises i Clinio')}</span> : null}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <p className="text-xs text-ds-muted">
        {t('medcom.epikriser.xsd', 'XSD-kontrol ved modtagelse (informativ)')}: {detail.row.xsd_ok === null ? '–' : detail.row.xsd_ok ? 'OK' : t('medcom.epikriser.xsd_failed', 'ikke gyldig – epikrisen er modtaget og vist alligevel')}
      </p>
    </article>
  );
}
